import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import { api, formatApiError, eur } from "../lib/api";
import { useApp, STOCK_LABELS } from "../context/AppContext";
import { EASE } from "../components/site/Motion";

const STEPS = ["Colore", "I tuoi dati", "Riepilogo", "Pagamento"];
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", cap: "", city: "", province: "", privacy: false, marketing: false };

export default function PreorderPage() {
  const [params] = useSearchParams();
  const { colours, config, colourSlug, setColour } = useApp();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Pre-ordina la tua SWAPA — SWAPA";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Preordina la tua SWAPA ZIP online: scegli il colore, inserisci i tuoi dati e blocca la vettura con €200, pagamento sicuro tramite Stripe.");
    const q = params.get("colore");
    if (q) setColour(q);
  }, [params, setColour]);

  const colour = useMemo(() => colours.find((c) => c.slug === colourSlug) || null, [colours, colourSlug]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    setError("");
    if (step === 1 && !colour) {
      setError("Scegli il colore della tua SWAPA per continuare.");
      return;
    }
    if (step === 2) {
      if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.cap || !form.city || !form.province) {
        setError("Compila tutti i campi obbligatori.");
        return;
      }
      if (!form.privacy) {
        setError("Il consenso privacy è obbligatorio per procedere con il preordine.");
        return;
      }
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const pay = async () => {
    setError("");
    setLoading(true);
    try {
      const pre = await api.post("/preorders", { ...form, colour_slug: colour.slug });
      const chk = await api.post("/payments/checkout", {
        reservation_id: pre.data.reservation_id,
        origin_url: window.location.origin,
      });
      window.location.href = chk.data.checkout_url;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:border-swapa-yellow focus:outline-none";

  return (
    <div data-testid="preorder-page" className="min-h-screen bg-ink pb-24 pt-32 sm:pt-40">
      <div className="mx-auto max-w-3xl px-5 sm:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">Pre-ordine</p>
        <h1 className="mt-4 font-display text-4xl font-black uppercase tracking-tighter text-white sm:text-6xl">
          La tua SWAPA ti aspetta.
        </h1>

        <ol className="mt-10 flex items-center gap-0 font-mono text-[9px] uppercase tracking-[0.15em]" data-testid="preorder-steps">
          {STEPS.map((s, i) => (
            <li key={s} className="flex flex-1 items-center gap-2">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center border ${step > i ? "border-swapa-yellow bg-swapa-yellow text-black" : "border-white/20 text-zinc-500"}`}>
                {i + 1}
              </span>
              <span className={`${step > i ? "text-white" : "text-zinc-600"} max-sm:hidden`}>{s}</span>
              {i < STEPS.length - 1 && <span className="mx-2 h-px flex-1 bg-white/10" aria-hidden="true" />}
            </li>
          ))}
        </ol>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="mt-10 border border-white/10 bg-card p-6 sm:p-10"
          >
            {step === 1 && (
              <div data-testid="preorder-step-1">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Scegli la tua SWAPA</h2>
                <p className="mt-2 text-sm text-zinc-400">Un'unica configurazione, full optional. Scegli solo l'espressione.</p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {colours.map((c) => {
                    const unavailable = c.stock === "unavailable";
                    const stock = STOCK_LABELS[c.stock];
                    return (
                      <button
                        key={c.slug}
                        data-testid={`preorder-colour-${c.slug}`}
                        disabled={unavailable}
                        onClick={() => setColour(c.slug)}
                        className={`flex items-center gap-4 border p-4 text-left transition-colors duration-300 ${
                          colourSlug === c.slug ? "border-swapa-yellow bg-white/5" : "border-white/10 hover:border-white/30"
                        } ${unavailable ? "cursor-not-allowed opacity-40" : ""}`}
                      >
                        <span className="h-10 w-10 shrink-0 rounded-full border border-white/20" style={{ backgroundColor: c.hex }} aria-hidden="true" />
                        <span>
                          <span className="block font-display text-base font-bold uppercase tracking-tight text-white">{c.name}</span>
                          {stock && <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">{stock.text}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="preorder-step-2">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">I tuoi dati</h2>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <input data-testid="preorder-firstname-input" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Nome *" className={inputCls} />
                  <input data-testid="preorder-lastname-input" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Cognome *" className={inputCls} />
                  <input data-testid="preorder-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className={inputCls} />
                  <input data-testid="preorder-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Cellulare *" className={inputCls} />
                  <input data-testid="preorder-cap-input" value={form.cap} onChange={(e) => set("cap", e.target.value)} placeholder="CAP *" className={inputCls} />
                  <input data-testid="preorder-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Città *" className={inputCls} />
                  <input data-testid="preorder-province-input" value={form.province} onChange={(e) => set("province", e.target.value)} placeholder="Provincia * (es. MI)" className={inputCls} />
                </div>
                <label className="mt-6 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-zinc-400">
                  <input data-testid="preorder-privacy-checkbox" type="checkbox" checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFD700]" />
                  Ho letto l'informativa privacy e acconsento al trattamento dei dati per la gestione del preordine. *
                </label>
                <label className="mt-3 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-zinc-400">
                  <input data-testid="preorder-marketing-checkbox" type="checkbox" checked={form.marketing} onChange={(e) => set("marketing", e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFD700]" />
                  Acconsento a ricevere comunicazioni marketing da SWAPA (facoltativo).
                </label>
              </div>
            )}

            {step === 3 && (
              <div data-testid="preorder-step-3">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Riepilogo ordine</h2>
                <div className="mt-8 space-y-4 border border-white/10 bg-ink p-6">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-3 text-sm text-white">
                      <span className="h-6 w-6 rounded-full border border-white/20" style={{ backgroundColor: colour?.hex }} aria-hidden="true" />
                      SWAPA™ — {colour?.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/10 pt-4 text-sm">
                    <span className="text-zinc-400">Prezzo del veicolo</span>
                    <span className="font-mono text-white">{eur(config.vehicle_price)} IVA incl.</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/10 pt-4">
                    <span className="text-sm text-zinc-400">Prenotazione online oggi</span>
                    <span className="font-display text-2xl font-bold text-swapa-yellow">{eur(config.deposit)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/10 pt-4 text-sm">
                    <span className="text-zinc-400">Importo residuo sul prezzo vettura</span>
                    <span className="font-mono text-white">{eur(config.vehicle_price - config.deposit)}</span>
                  </div>
                </div>
                <div data-testid="preorder-terms" className="mt-6 space-y-3 border border-dashed border-zinc-700 p-5 text-xs leading-relaxed text-zinc-500">
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">Termini della prenotazione online</p>
                  <p>{config.terms?.refundable}</p>
                  <p>{config.terms?.deduction}</p>
                  <p>{config.terms?.delivery}</p>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="preorder-step-4">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Pagamento sicuro</h2>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  Verrai reindirizzato a Stripe per completare il pagamento della caparra di {eur(config.deposit)}
                  in modo sicuro: carta di credito/debito, Apple Pay e Google Pay dove disponibili.
                  I dati della carta non passano mai dai nostri server.
                </p>
                <div className="mt-8 flex items-center justify-between border border-white/10 bg-ink p-5">
                  <span className="text-sm text-zinc-400">Totale da pagare ora</span>
                  <span className="font-display text-3xl font-bold text-white">{eur(config.deposit)}</span>
                </div>
                <button
                  data-testid="preorder-pay-button"
                  onClick={pay}
                  disabled={loading}
                  className="mt-8 flex w-full items-center justify-center gap-3 bg-swapa-yellow py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                >
                  <Lock className="h-4 w-4" />
                  {loading ? "Reindirizzamento a Stripe…" : `Paga ${eur(config.deposit)} con Stripe`}
                </button>
              </div>
            )}

            {error && <p data-testid="preorder-error" className="mt-6 text-sm text-red-400">{error}</p>}

            {step < 4 && (
              <div className="mt-10 flex items-center justify-between">
                <button
                  data-testid="preorder-back-button"
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  disabled={step === 1}
                  className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-white disabled:opacity-30"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Indietro
                </button>
                <button
                  data-testid="preorder-step-next-button"
                  onClick={next}
                  className="inline-flex items-center gap-2 bg-swapa-yellow px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Continua <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
